import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { projectFirestore } from "@/firebase/config";

// Store active session in Firestore
const storeActiveSession = async (userInfo, deviceInfo, sessionToken) => {
  try {
    const activeSessionsCollection = collection(
      projectFirestore,
      "activeSessions"
    );

    const sessionData = {
      userId: userInfo.uid,
      email: userInfo.email,
      displayName: userInfo.displayName || null,
      sessionToken: sessionToken,
      deviceInfo: {
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        platform: deviceInfo.platform,
        userAgent: deviceInfo.userAgent,
        ip: deviceInfo.ip,
      },
      loginTimestamp: serverTimestamp(),
      lastActivity: serverTimestamp(),
      isActive: true,
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(activeSessionsCollection, sessionData);

    // Store session ID in localStorage for current device
    localStorage.setItem("sessionId", docRef.id);

    return { success: true, sessionId: docRef.id };
  } catch (error) {
    console.error("Error storing active session:", error);
    return { success: false, error: error.message };
  }
};

// Get active sessions for a user or all users
const getActiveSessions = async (userId = null) => {
  try {
    const activeSessionsCollection = collection(
      projectFirestore,
      "activeSessions"
    );
    let q;

    if (userId) {
      q = query(
        activeSessionsCollection,
        where("userId", "==", userId),
        where("isActive", "==", true)
      );
    } else {
      q = query(activeSessionsCollection, where("isActive", "==", true));
    }

    const querySnapshot = await getDocs(q);
    const sessions = [];

    querySnapshot.forEach((doc) => {
      sessions.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return { success: true, sessions };
  } catch (error) {
    console.error("Error fetching active sessions:", error);
    return { success: false, error: error.message };
  }
};

// Revoke specific session
const revokeSession = async (sessionId) => {
  try {
    const sessionRef = doc(projectFirestore, "activeSessions", sessionId);

    await updateDoc(sessionRef, {
      isActive: false,
      revokedAt: serverTimestamp(),
      revokedBy: "admin", // You can pass the admin ID here
    });

    return { success: true };
  } catch (error) {
    console.error("Error revoking session:", error);
    return { success: false, error: error.message };
  }
};

// Revoke all sessions for a specific user
const revokeUserSessions = async (userId) => {
  try {
    const activeSessionsCollection = collection(
      projectFirestore,
      "activeSessions"
    );
    const q = query(
      activeSessionsCollection,
      where("userId", "==", userId),
      where("isActive", "==", true)
    );

    const querySnapshot = await getDocs(q);
    const batch = [];

    querySnapshot.forEach((sessionDoc) => {
      batch.push(
        updateDoc(sessionDoc.ref, {
          isActive: false,
          revokedAt: serverTimestamp(),
          revokedBy: "admin",
        })
      );
    });

    await Promise.all(batch);

    return { success: true, revokedCount: batch.length };
  } catch (error) {
    console.error("Error revoking user sessions:", error);
    return { success: false, error: error.message };
  }
};

// Revoke all active sessions (logout everyone)
const revokeAllSessions = async () => {
  try {
    const activeSessionsCollection = collection(
      projectFirestore,
      "activeSessions"
    );
    const q = query(activeSessionsCollection, where("isActive", "==", true));

    const querySnapshot = await getDocs(q);
    const batch = [];

    querySnapshot.forEach((sessionDoc) => {
      batch.push(
        updateDoc(sessionDoc.ref, {
          isActive: false,
          revokedAt: serverTimestamp(),
          revokedBy: "admin",
        })
      );
    });

    await Promise.all(batch);

    return { success: true, revokedCount: batch.length };
  } catch (error) {
    console.error("Error revoking all sessions:", error);
    return { success: false, error: error.message };
  }
};

// Update session activity
const updateSessionActivity = async (sessionId) => {
  try {
    if (!sessionId) return { success: false, error: "No session ID provided" };

    const sessionRef = doc(projectFirestore, "activeSessions", sessionId);

    await updateDoc(sessionRef, {
      lastActivity: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error("Error updating session activity:", error);
    return { success: false, error: error.message };
  }
};

// Check if current session is still active
const checkSessionStatus = async (sessionId) => {
  try {
    if (!sessionId) return { success: false, isActive: false };

    const sessionRef = doc(projectFirestore, "activeSessions", sessionId);
    const sessionDoc = await getDocs(
      query(
        collection(projectFirestore, "activeSessions"),
        where("__name__", "==", sessionId)
      )
    );

    if (sessionDoc.empty) {
      return { success: true, isActive: false };
    }

    const sessionData = sessionDoc.docs[0].data();
    return { success: true, isActive: sessionData.isActive };
  } catch (error) {
    console.error("Error checking session status:", error);
    return { success: false, error: error.message };
  }
};

// Clean up expired sessions (older than 30 days and inactive)
const cleanupExpiredSessions = async () => {
  try {
    const activeSessionsCollection = collection(
      projectFirestore,
      "activeSessions"
    );
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const q = query(
      activeSessionsCollection,
      where("lastActivity", "<", thirtyDaysAgo)
    );

    const querySnapshot = await getDocs(q);
    const batch = [];

    querySnapshot.forEach((sessionDoc) => {
      batch.push(deleteDoc(sessionDoc.ref));
    });

    await Promise.all(batch);

    return { success: true, cleanedCount: batch.length };
  } catch (error) {
    console.error("Error cleaning up expired sessions:", error);
    return { success: false, error: error.message };
  }
};

export {
  storeActiveSession,
  getActiveSessions,
  revokeSession,
  revokeUserSessions,
  revokeAllSessions,
  updateSessionActivity,
  checkSessionStatus,
  cleanupExpiredSessions,
};
